'use client'

import React from 'react'
import Link from 'next/link'
import clsx from 'clsx'
import { useRouter } from 'next/navigation'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { useLoginMutation } from '@/api/client'

import { Form } from '@/components/form/Form'
import { FormInput } from '@/components/form/input/FormInput'
import { Stack } from '@/components/layout/stack'
import { Button } from '@/components/inputs/button'
import { Box } from '@/components/layout/box'
import { Text } from '@/components/typography/text'
import { FormCheckbox } from '@/components/form/form-checkbox/FormCheckbox'
import { Spinner } from '@/components/icons/spinner/Spinner'
import { link, linkGroup } from '@/components/navigation/link/Link.css'
import { FormPasswordInput } from '@/components/form/input/FormPasswordInput'
import { appRoutes } from '@/const'
import { useAuthStore } from '@/store/auth-store'
import { signInSchema, SignInSchema } from '@/screens/auth/sign-in/(helpers)/sign-in-schema'

import * as styles from '@/screens/auth/sign-in/SignIn.css'

interface Props {
	role: 'patient' | 'provider' | 'teamMember'
}

export const SignInForm = ({ role }: Props) => {
	const [loading, setLoading] = React.useState<boolean>(false)
	const { setAuthValues } = useAuthStore()
	const loginMutation = useLoginMutation()
	const router = useRouter()

	const form = useForm<SignInSchema>({
		resolver: zodResolver(signInSchema),
		defaultValues: {
			email: '',
			password: '',
			keepMeLoggedIn: false
		}
	})

	const submit = async (data: SignInSchema) => {
		try {
			setLoading(true)
			const res = await loginMutation.mutateAsync({
				role: role,
				body: { email: data.email, password: data.password, keepMeLoggedIn: data.keepMeLoggedIn, authType: 'Password' }
			})
			if (res.data.email) {
				setAuthValues({ role: 'provider', email: res.data.email, keepMeLoggedIn: data.keepMeLoggedIn })
				if (res.data.twoStepVerification) {
					router.push(appRoutes.auth.signIn.verify)
				} else {
					if (role === 'teamMember') {
						router.push(appRoutes.teamMember.dashboard.schedulingTemplate)
					} else {
						router.push(appRoutes.home)
					}
				}
			}
		} catch (e) {
			console.error(e)
		} finally {
			setLoading(false)
		}
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(submit)} className={styles.signInFormContainer}>
				<Stack gap={6}>
					<FormInput<SignInSchema> name="email" label="Email" placeholder="Email" />
					<FormPasswordInput<SignInSchema> name="password" label="Password" type="password" placeholder="Password" />
					<Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={6}>
						<FormCheckbox<SignInSchema> name="keepMeLoggedIn" label="Keep me signed in" />
						<Link href={appRoutes.auth.signIn.forgotPassword} className={linkGroup}>
							<Text as="span" variant="paragraph2" className={clsx(link, styles.forgotPassword)}>
								Forgot password?
							</Text>
						</Link>
					</Box>
				</Stack>
				<Button type="submit" disabled={loading}>
					{loading && <Spinner spinnerColor="white" />}
					Sign In
				</Button>
			</form>
		</Form>
	)
}
